/**
 * Shared open state for the desktop catalog mega-menu (Phase 5 / Phase 9 a11y).
 *
 * `useState` so Catalog trigger (AppHeader) and overlay share one flag under SSR.
 * Close-on-route mirrors cart/search patterns. Header freeze (R13/S40) is applied
 * by the caller via `useHeaderChrome().setFrozen(isOpen)` — see AppHeader.
 * Focus restore (S32): capture opener on open; Esc/toggle restore to Catalog
 * when still mounted; route close skips restore.
 */

import { createFocusRestore } from '#Common/composables/useFocusRestore'

const TRIGGER_SELECTOR = '.app-header-catalog'

/** One restore helper for all useCatalogMenu() callers (useState-backed open flag). */
const catalogFocus = createFocusRestore()

export function useCatalogMenu() {
  const isOpen = useState('catalog-menu-open', () => false)
  const selectedRootId = useState<number | null>(
    'catalog-menu-selected-root',
    () => null,
  )

  function open() {
    if (import.meta.client) {
      const desktop = window.matchMedia('(min-width: 1024px)').matches
      if (!desktop) return

      const active = document.activeElement
      const trigger = document.querySelector<HTMLElement>(TRIGGER_SELECTOR)
      catalogFocus.capture(
        active instanceof HTMLElement && active.closest(TRIGGER_SELECTOR)
          ? active
          : trigger,
      )
    }

    isOpen.value = true
  }

  function close(options?: { restoreFocus?: boolean }) {
    if (!isOpen.value) return

    const restoreFocus = options?.restoreFocus !== false

    isOpen.value = false
    selectedRootId.value = null

    if (restoreFocus) {
      catalogFocus.restore()
      // Fallback if opener unmounted (header remount) — Catalog button if present.
      if (import.meta.client) {
        nextTick(() => {
          const active = document.activeElement
          if (active === document.body || active == null) {
            document.querySelector<HTMLElement>(TRIGGER_SELECTOR)?.focus()
          }
        })
      }
    }
    else {
      catalogFocus.clear()
    }
  }

  function toggle() {
    if (isOpen.value) {
      close()
    }
    else {
      open()
    }
  }

  function selectRoot(id: number) {
    selectedRootId.value = id
  }

  // Register per caller (AppHeader + MegaMenu). close() is idempotent; a useState
  // "watchers ready" gate was dropping the watch after HMR/remount while leaving
  // the flag true — menu then stayed open after link navigation.
  const route = useRoute()
  watch(
    () => route.fullPath,
    () => {
      if (isOpen.value) {
        // Navigating from a menu link — do not steal focus back to Catalog.
        close({ restoreFocus: false })
      }
    },
  )

  return {
    isOpen: readonly(isOpen),
    selectedRootId: readonly(selectedRootId),
    open,
    close,
    toggle,
    selectRoot,
    triggerSelector: TRIGGER_SELECTOR,
  }
}
